import React, { useState, useCallback } from 'react';
import { theme } from '../../theme';
import { useLanguage } from '../../i18n/useLanguage';
import { shouldShowDeploymentQuestion, infrastructureToDeployment } from '../../utils/sovScoreCalculations';
import {
  OTHER_PROVIDER_SENTINEL,
  OTHER_CATEGORIES,
  isOtherProvider,
  getOtherCategory,
  buildOtherProviderName,
} from '../../utils/otherProviderUtils';
import { OperationsScoringStep } from './OperationsScoringStep';
import type { OtherCategory } from '../../utils/otherProviderUtils';
import type {
  WorkplaceLevel,
  AggregatedProvider,
  SovScoresData,
  InfrastructureMode,
  DeploymentModel,
  IstSelection,
  SubPoint,
  OperationsAnswers,
} from '../../types/workplace';

interface IstZustandStepProps {
  levels: WorkplaceLevel[];
  aggregatedProviders: AggregatedProvider[];
  sovData: SovScoresData;
  infrastructureMode: InfrastructureMode;
  globalDeployment: DeploymentModel | null;
  istSelections: Record<string, IstSelection>;
  operationsAnswers: OperationsAnswers;
  onOperationsAnswer: (qId: 'q1' | 'q2' | 'q3' | 'q4', sealValue: number) => void;
  onSelectProvider: (featureGroupId: string, provider: string | null, deployment?: DeploymentModel) => void;
  onSelectDeployment: (featureGroupId: string, deployment: DeploymentModel) => void;
  onContinue: () => void;
  onBack: () => void;
}

const DEPLOYMENT_LABEL_KEYS: Record<DeploymentModel, string> = {
  saas: 'workplace.deployment.saas',
  'hosted-eu': 'workplace.deployment.hostedEu',
  'hosted-non-eu': 'workplace.deployment.hostedNonEu',
  'self-hosted': 'workplace.deployment.selfHosted',
};

const ALL_DEPLOYMENTS: DeploymentModel[] = ['saas', 'hosted-eu', 'hosted-non-eu', 'self-hosted'];

const CATEGORY_I18N: Record<string, string> = {
  'EU Open Source': 'workplace.other.euOpenSource',
  'Non-EU Open Source': 'workplace.other.nonEuOpenSource',
  'EU Provider (Proprietary)': 'workplace.other.euProviderProprietary',
  'Non-EU Provider (Proprietary)': 'workplace.other.nonEuProviderProprietary',
};

export const IstZustandStep: React.FC<IstZustandStepProps> = ({
  levels,
  aggregatedProviders,
  sovData,
  infrastructureMode,
  globalDeployment,
  istSelections,
  operationsAnswers,
  onOperationsAnswer,
  onSelectProvider,
  onSelectDeployment,
  onContinue,
  onBack,
}) => {
  const { t } = useLanguage();
  const [expandedLevels, setExpandedLevels] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    levels.forEach(l => { init[l.id] = true; });
    return init;
  });

  // Track which sub-points have the "Other" sentinel selected (before category choice)
  const [otherPending, setOtherPending] = useState<Record<string, boolean>>({});

  // Progress count: provider-selection sub-points with a selection + operations-scoring sub-points with all 4 answers
  const allSubPoints = levels.flatMap(l => l.subPoints);
  const assignedCount = allSubPoints.filter(sp => {
    if (sp.type === 'provider-selection') {
      return !!istSelections[sp.id];
    }
    if (sp.type === 'operations-scoring') {
      return operationsAnswers.q1 !== null && operationsAnswers.q2 !== null &&
             operationsAnswers.q3 !== null && operationsAnswers.q4 !== null;
    }
    return false;
  }).length;
  const totalCount = allSubPoints.length;

  const toggleLevel = useCallback((levelId: string) => {
    setExpandedLevels(prev => ({ ...prev, [levelId]: !prev[levelId] }));
  }, []);

  const getEligibleProviders = useCallback(
    (subPoint: SubPoint) => {
      const eligibleNames = new Set(subPoint.eligibleProviders ?? []);
      return aggregatedProviders.filter(p => eligibleNames.has(p.name) && !isOtherProvider(p.name));
    },
    [aggregatedProviders],
  );

  const handleProviderChange = useCallback(
    (subPointId: string, providerName: string | null) => {
      if (!providerName) {
        onSelectProvider(subPointId, null);
        setOtherPending(prev => ({ ...prev, [subPointId]: false }));
        return;
      }
      if (providerName === OTHER_PROVIDER_SENTINEL) {
        // Don't persist sentinel — just show the category picker
        setOtherPending(prev => ({ ...prev, [subPointId]: true }));
        // Clear any existing selection for this sub-point
        onSelectProvider(subPointId, null);
        return;
      }
      // Regular provider
      setOtherPending(prev => ({ ...prev, [subPointId]: false }));
      const depQ = shouldShowDeploymentQuestion(providerName, infrastructureMode, globalDeployment, sovData);
      const deployment = depQ.autoDeployment ?? depQ.availableDeployments[0] ?? 'saas';
      onSelectProvider(subPointId, providerName, deployment);
    },
    [onSelectProvider, infrastructureMode, globalDeployment, sovData],
  );

  const handleOtherCategory = useCallback(
    (subPointId: string, category: OtherCategory) => {
      const compositeName = buildOtherProviderName(category);
      const deployment = infrastructureToDeployment(infrastructureMode) ?? 'saas';
      onSelectProvider(subPointId, compositeName, deployment);
      setOtherPending(prev => ({ ...prev, [subPointId]: false }));
    },
    [onSelectProvider, infrastructureMode],
  );

  const handleDeploymentChange = useCallback(
    (subPointId: string, deployment: DeploymentModel) => {
      onSelectDeployment(subPointId, deployment);
    },
    [onSelectDeployment],
  );

  return (
    <div style={{ maxWidth: 750, margin: '0 auto', padding: 16 }}>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: theme.text }}>
        {t('workplace.istZustand.title')}
      </h2>
      <p style={{ margin: '6px 0 4px', fontSize: 14, color: theme.muted }}>
        {t('workplace.istZustand.description')}
      </p>
      <p style={{ margin: '0 0 16px', fontSize: 13, fontWeight: 600, color: theme.accent }}>
        {t('workplace.istZustand.progress', { count: assignedCount, total: totalCount })}
      </p>

      {levels.map(level => {
        const open = expandedLevels[level.id] ?? true;
        return (
          <div key={level.id} style={{ marginBottom: 12, border: `1px solid ${theme.border}`, borderRadius: 6, overflow: 'hidden' }}>
            <button
              onClick={() => toggleLevel(level.id)}
              style={{
                width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px', background: theme.cardBg, border: 'none', cursor: 'pointer',
                fontSize: 15, fontWeight: 700, color: theme.text,
              }}
            >
              <span>{level.title}</span>
              <span style={{ fontSize: 12 }}>{open ? '▲' : '▼'}</span>
            </button>

            {open && (
              <div style={{ padding: '8px 14px 14px' }}>
                {level.subPoints.map(subPoint => {
                  // --- Operations Scoring sub-point ---
                  if (subPoint.type === 'operations-scoring') {
                    return (
                      <div key={subPoint.id} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${theme.border}` }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 8 }}>{subPoint.title}</div>
                        <OperationsScoringStep
                          answers={operationsAnswers}
                          onAnswer={onOperationsAnswer}
                        />
                      </div>
                    );
                  }

                  // --- Provider Selection sub-point ---
                  const sel = istSelections[subPoint.id];
                  const eligible = getEligibleProviders(subPoint);
                  const selectedProvider = sel?.provider ?? '';
                  const isOtherSelected = isOtherProvider(selectedProvider);
                  const isPending = otherPending[subPoint.id] ?? false;
                  const activeCategory = isOtherSelected ? getOtherCategory(selectedProvider) : null;

                  // Determine dropdown value: show sentinel when Other is active or pending
                  const dropdownValue = isOtherSelected || isPending ? OTHER_PROVIDER_SENTINEL : selectedProvider;

                  // Deployment logic for non-Other providers
                  let depInfo: { show: boolean; availableDeployments: DeploymentModel[]; autoDeployment: DeploymentModel | null } | null = null;
                  if (selectedProvider && !isOtherSelected) {
                    depInfo = shouldShowDeploymentQuestion(selectedProvider, infrastructureMode, globalDeployment, sovData);
                  }
                  const showRegularDeployment = selectedProvider && !isOtherSelected && (
                    depInfo?.show ||
                    (infrastructureMode === 'hybrid' && depInfo)
                  );
                  const deploymentOptions = depInfo?.availableDeployments ?? [];

                  return (
                    <div key={subPoint.id} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${theme.border}` }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 4 }}>{subPoint.title}</div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        <select
                          value={dropdownValue}
                          onChange={e => handleProviderChange(subPoint.id, e.target.value || null)}
                          style={{
                            flex: '1 1 200px', padding: '8px 10px', border: `1px solid ${theme.border}`,
                            borderRadius: 4, fontSize: 13, background: '#fff',
                          }}
                        >
                          <option value="">{t('workplace.istZustand.unassigned')}</option>
                          {eligible.map(p => (
                            <option key={p.name} value={p.name}>{p.name}</option>
                          ))}
                          <option disabled>
                            {t('workplace.other.divider' as any)}
                          </option>
                          <option value={OTHER_PROVIDER_SENTINEL}>
                            {t('workplace.other.label' as any)}
                          </option>
                        </select>

                        {/* Regular provider deployment */}
                        {showRegularDeployment && deploymentOptions.length > 1 && (
                          <select
                            value={sel?.deployment ?? ''}
                            onChange={e => handleDeploymentChange(subPoint.id, e.target.value as DeploymentModel)}
                            style={{
                              flex: '1 1 180px', padding: '8px 10px', border: `1px solid ${theme.border}`,
                              borderRadius: 4, fontSize: 13, background: '#fff',
                            }}
                          >
                            <option value="" disabled>{t('workplace.istZustand.selectDeployment')}</option>
                            {deploymentOptions.map(d => (
                              <option key={d} value={d}>{t(DEPLOYMENT_LABEL_KEYS[d] as any)}</option>
                            ))}
                          </select>
                        )}
                        {showRegularDeployment && deploymentOptions.length === 1 && (
                          <span style={{
                            padding: '8px 12px', background: theme.cardBg, border: `1px solid ${theme.border}`,
                            borderRadius: 4, fontSize: 12, color: theme.muted,
                          }}>
                            {t(DEPLOYMENT_LABEL_KEYS[deploymentOptions[0]] as any)}
                          </span>
                        )}
                      </div>

                      {/* "Other" inline category + deployment selection */}
                      {(isPending || isOtherSelected) && (
                        <div style={{ marginTop: 8, padding: '8px 12px', background: '#f9f9f9', borderRadius: 6, border: `1px solid ${theme.border}` }}>
                          <div style={{ fontSize: 12, color: theme.muted, marginBottom: 6 }}>
                            {t('workplace.other.selectCategory' as any)}:
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                            {OTHER_CATEGORIES.map(cat => (
                              <button
                                key={cat}
                                onClick={() => handleOtherCategory(subPoint.id, cat)}
                                style={{
                                  padding: '6px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                                  cursor: 'pointer',
                                  border: activeCategory === cat ? `2px solid ${theme.secondary}` : `1px solid ${theme.border}`,
                                  background: activeCategory === cat ? theme.primarySoft : '#fff',
                                  color: theme.text,
                                }}
                              >
                                {t(CATEGORY_I18N[cat] as any)}
                              </button>
                            ))}
                          </div>
                          {isOtherSelected && (
                            <select
                              value={sel?.deployment ?? ''}
                              onChange={e => handleDeploymentChange(subPoint.id, e.target.value as DeploymentModel)}
                              style={{
                                padding: '8px 10px', border: `1px solid ${theme.border}`,
                                borderRadius: 4, fontSize: 13, background: '#fff', width: '100%',
                              }}
                            >
                              <option value="" disabled>{t('workplace.istZustand.selectDeployment')}</option>
                              {ALL_DEPLOYMENTS.map(d => (
                                <option key={d} value={d}>{t(DEPLOYMENT_LABEL_KEYS[d] as any)}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button
          onClick={onBack}
          style={{
            padding: '10px 24px', border: `1px solid ${theme.border}`, background: '#fff',
            color: theme.text, fontWeight: 700, fontSize: 15, borderRadius: 4, cursor: 'pointer',
          }}
        >
          {t('workplace.istZustand.back')}
        </button>
        <button
          onClick={onContinue}
          style={{
            padding: '10px 24px', border: `1px solid ${theme.primaryBorder}`,
            background: theme.primarySoft, color: theme.text,
            fontWeight: 700, fontSize: 15, borderRadius: 4, cursor: 'pointer',
          }}
        >
          {t('workplace.istZustand.continue')}
        </button>
      </div>
    </div>
  );
};
